/*  Pricing page — CMS-driven marketing page.
    ────────────────────────────────────────────────────────────────
    iter100.152 — Rewritten to consume `db.cms_content` under page
    slug `pricing`. Every headline, sub-copy, complexity tier,
    cost factor, list item, FAQ, and testimonial string now lives in
    the DB and is editable via Admin → Landing → Pricing Page.

    Behavioural state (complexity toggle, FAQ open index, sticky CTA
    visibility) remains client-side — that's UX plumbing, not content.

    A deliberately price-less pricing page. The number is set by your
    doctor at consultation. */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import CounsellorCallbackCard from "@/components/site/CounsellorCallbackCard";
import SeoHead from "@/components/site/SeoHead";
import { useProductPageCms } from "@/lib/useProductPageCms";
import api from "@/lib/api";
import {
    Sparkle, Question, ShieldCheck, ArrowRight, CheckCircle,
    Clock, Stack, Wallet, Heart, CalendarCheck, Stethoscope,
    Lightning, CaretDown, FileText, SealCheck, Quotes, X,
    Crown, Infinity as InfinityIcon,
} from "@phosphor-icons/react";


const CMS_PAGE = "pricing";

/* Icon lookup for CMS-supplied `icon` strings. Keep in sync with the
   admin help text in `pricing_page_seed.py`. Unknown names fall back
   to a neutral Sparkle so a typo can't blank a card. */
const ICON_MAP = {
    Sparkle, Question, ShieldCheck, ArrowRight, CheckCircle,
    Clock, Stack, Wallet, Heart, CalendarCheck, Stethoscope,
    Lightning, CaretDown, FileText, SealCheck, Quotes, X,
    Crown, InfinityIcon,
};
const resolveIcon = (name) => ICON_MAP[name] || Sparkle;


/* ── Safety-net defaults used when the CMS payload hasn't loaded or a
   key is missing. These mirror the seed file so the page never renders
   blank during the initial fetch or on API failure. ────────────── */
const DEFAULT_COMPLEXITY_TIERS = [
    {
        id: "simple", label: "Simple", tone: "bg-emerald-500 text-white",
        duration: "6 to 9 months", aligners: "10 to 20 aligners",
        typical: "Mild crowding, small gaps, single arch alignment",
        mechanics: ["Attachments (occasional)", "Minimal IPR"],
    },
    {
        id: "moderate", label: "Moderate", tone: "bg-amber-500 text-white",
        duration: "9 to 15 months", aligners: "20 to 30 aligners",
        typical: "Both arches, deep bite, midline correction, Class II tendency",
        mechanics: ["Attachments", "IPR", "Elastics (some)"],
    },
    {
        id: "complex", label: "Complex", tone: "bg-rose-500 text-white",
        duration: "15+ months", aligners: "30+ aligners",
        typical: "Significant crowding or spacing, Class II/III, open bite, crossbite, refinements likely",
        mechanics: ["Attachments", "IPR", "Class II/III mechanics", "Auxiliaries"],
    },
];


/* ================================================================
   Page
   ================================================================ */
export default function Pricing() {
    const { t, arr, loading } = useProductPageCms(CMS_PAGE);
    const tiers = arr("complexity.tiers_json", DEFAULT_COMPLEXITY_TIERS);
    const tiersById = React.useMemo(() => {
        const m = {};
        for (const tier of tiers) m[tier.id] = tier;
        return m;
    }, [tiers]);

    // Default to the middle tier if it exists, else the first.
    const initialId = tiersById.moderate ? "moderate" : (tiers[0]?.id || "moderate");
    const [complexityId, setComplexityId] = useState(initialId);

    // If the CMS payload arrives after mount and reshapes the tiers, make
    // sure the selected id remains valid.
    useEffect(() => {
        if (!tiersById[complexityId] && tiers.length > 0) {
            setComplexityId(tiers[0].id);
        }
    }, [tiers, tiersById, complexityId]);

    const handleHeroComplexityChange = (id) => {
        setComplexityId(id);
        requestAnimationFrame(() => {
            const el = document.querySelector('[data-testid="complexity-panel"]');
            if (!el) return;
            const top = el.getBoundingClientRect().top + window.scrollY - 96;
            window.scrollTo({ top, behavior: "smooth" });
        });
    };

    if (loading) {
        return <div className="min-h-[60vh] bg-white" aria-hidden="true" data-testid="pricing-loading" />;
    }

    return (
        <div data-testid="pricing-page">
            <SeoHead
                page={CMS_PAGE}
                fallbackTitle="Pricing · Invisique™ Clear Aligners"
                fallbackDescription="Honest, per-case aligner pricing from certified Invisique™ orthodontists."
            />
            <Hero
                t={t} arr={arr}
                tiers={tiers}
                tiersById={tiersById}
                complexityId={complexityId}
                onComplexityChange={handleHeroComplexityChange}
            />
            <ComplexityExplorer
                t={t} arr={arr}
                tiers={tiers}
                tiersById={tiersById}
                complexityId={complexityId}
                onComplexityChange={setComplexityId}
            />
            <CostFactors t={t} arr={arr} />
            <IncludedSection t={t} arr={arr} />
            <HonestQuoteSample t={t} arr={arr} />
            <RetainerLifecycle t={t} arr={arr} />
            <FinancingStrip t={t} />
            <FaqSection t={t} arr={arr} />
            <PricingTestimonial t={t} />
            <CounsellorBlock t={t} />
            <StickyAssessmentBar t={t} />
        </div>
    );
}


/* ================================================================
   Hero
   ================================================================ */
function Hero({ t, arr, tiers, tiersById, complexityId, onComplexityChange }) {
    const sample = tiersById[complexityId] || tiers[0] || DEFAULT_COMPLEXITY_TIERS[1];
    const trustPillars = arr("hero.trust_strip_json", []);
    const miniStats = arr("sample_card.mini_stats_json", []);

    return (
        <section className="relative overflow-hidden" data-testid="pricing-hero">
            <div className="absolute inset-0 blue-grid-bg opacity-60 mask-fade-b pointer-events-none" />

            <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 lg:pt-24 pb-16 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center relative">
                <div className="lg:col-span-7">
                    <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white border border-border/70 shadow-sm mb-6" data-testid="pricing-trust-pill">
                        <ShieldCheck size={14} weight="duotone" color="hsl(192 100% 31%)" />
                        <span className="text-xs font-semibold text-foreground">
                            {t("hero.trust_pill_prefix", "Written quote")}{" "}
                            <span className="text-muted-foreground font-normal">
                                · {t("hero.trust_pill_suffix", "no starting-at gimmicks")}
                            </span>
                        </span>
                    </div>

                    <div className="overline text-primary mb-5">{t("hero.eyebrow", "Pricing, the honest way")}</div>

                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
                        {t("hero.title_line1", "Your smile. Your case.")}<br />
                        <span className="text-primary">{t("hero.title_line2", "Your quote.")}</span>
                    </h1>

                    <p className="mt-6 text-lg text-muted-foreground max-w-xl">
                        {t("hero.blurb", "We don't list a single price, and we're proud of that. Aligner treatment is a clinical service shaped by your case, not a catalogue item. The number you pay is the number your doctor writes for you.")}
                    </p>

                    {trustPillars.length > 0 && (
                        <div className="mt-9 grid sm:grid-cols-3 gap-3" data-testid="pricing-hero-trust-strip">
                            {trustPillars.map((p) => {
                                const Icon = resolveIcon(p.icon);
                                const slug = String(p.title || "").toLowerCase().replace(/[^a-z]+/g, "-");
                                return (
                                    <div
                                        key={p.title}
                                        className="flex items-start gap-3 px-3.5 py-3 rounded-2xl bg-card border border-border/60 hover:border-primary/30 transition-colors"
                                        data-testid={`pricing-hero-trust-${slug}`}
                                    >
                                        <div className="w-9 h-9 rounded-full bg-primary text-white grid place-items-center shadow-sm ring-2 ring-secondary/40 shrink-0 mt-0.5">
                                            <Icon size={16} weight="fill" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-display text-[13px] font-bold leading-tight">{p.title}</div>
                                            <div className="text-[11.5px] text-muted-foreground leading-snug mt-0.5">{p.desc}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Right column — Sample quote preview */}
                <div className="lg:col-span-5 relative">
                    <div className="absolute -top-8 -right-8 w-72 h-72 rounded-full bg-primary/15 blur-3xl pointer-events-none" aria-hidden="true" />
                    <div className="absolute -bottom-12 -left-10 w-64 h-64 rounded-full bg-amber-200/40 blur-3xl pointer-events-none" aria-hidden="true" />

                    <div className="relative">
                        <div className="absolute -top-3 -left-3 z-10 hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground/90 backdrop-blur text-white shadow-lg rotate-[-4deg]" data-testid="pricing-sample-badge">
                            <Sparkle size={12} weight="fill" className="text-amber-300" />
                            <span className="text-[11px] font-bold tracking-wider uppercase">
                                {t("sample_card.badge_label", "Sample preview")}
                            </span>
                        </div>

                        <div className="absolute -top-3 -right-3 z-10 hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-lg border border-border/70">
                            <SealCheck size={14} weight="duotone" color="hsl(192 100% 31%)" />
                            <span className="text-[11px] font-semibold tracking-wide">
                                {t("sample_card.verified_label", "Doctor verified")}
                            </span>
                        </div>

                        <div className="relative bg-white text-foreground rounded-3xl p-7 shadow-[0_20px_60px_-15px_rgba(0,45,57,0.25)] ring-1 ring-white/40 border border-border/40" data-testid="pricing-sample-card">
                            <div className="flex items-center justify-between mb-5 pb-4 border-b border-border">
                                <div className="flex items-center gap-2">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <FileText size={20} weight="duotone" className="text-primary" />
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                            {t("sample_card.receipt_title", "Your written quote")}
                                        </div>
                                        <div className="text-xs font-mono text-foreground/70">
                                            {t("sample_card.receipt_id", "INV-QUOTE-•••••")}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 flex items-center gap-1.5">
                                    <span>{t("sample_card.toggle_hint", "Try a different complexity")}</span>
                                    <span className="text-amber-500" aria-hidden="true">↓</span>
                                </div>
                                <div className="inline-flex bg-secondary/70 rounded-full p-0.5 w-full" data-testid="pricing-sample-toggle">
                                    {tiers.map((opt) => (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            onClick={() => onComplexityChange(opt.id)}
                                            data-testid={`pricing-sample-toggle-${opt.id}`}
                                            aria-pressed={complexityId === opt.id}
                                            className={`flex-1 text-[11px] font-bold tracking-wider uppercase rounded-full h-8 transition-all ${
                                                complexityId === opt.id
                                                    ? opt.tone + " shadow-sm"
                                                    : "text-foreground/60 hover:text-foreground"
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-3 text-sm" data-testid="pricing-sample-rows">
                                <Row
                                    label="Complexity"
                                    value={
                                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full transition-colors ${sample.tone}`}>
                                            {sample.label}
                                        </span>
                                    }
                                />
                                <Row label="Duration" value={sample.duration} />
                                <Row label="Aligners" value={sample.aligners} />
                                <Row label="Refinements" value={t("sample_card.refinements_value", "Included*")} />
                                <Row label="Plan revisions" value={t("sample_card.plan_revisions_value", "Unlimited")} />
                            </div>

                            <div className="mt-5 pt-4 border-t border-dashed border-border">
                                <div className="flex items-center justify-between">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                        {t("sample_card.final_label", "Final number")}
                                    </div>
                                    <div className="font-display font-bold text-base text-primary">
                                        {t("sample_card.final_value", "Your doctor")}
                                    </div>
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-1.5 italic">
                                    {t("sample_card.final_note", "Personalised at consultation. No catalogue tiers.")}
                                </p>
                            </div>

                            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                                {miniStats.map((m, i) => (
                                    <MiniStat key={i} top={m.top} bot={m.bot} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const Row = ({ label, value }) => (
    <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="font-semibold text-sm">{value}</span>
    </div>
);
const MiniStat = ({ top, bot }) => (
    <div className="bg-secondary/60 rounded-lg py-2">
        <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{top}</div>
        <div className="text-xs font-bold text-foreground mt-0.5">{bot}</div>
    </div>
);


/* ================================================================
   Complexity Explorer
   ================================================================ */
function ComplexityExplorer({ t, arr, tiers, tiersById, complexityId, onComplexityChange }) {
    const c = tiersById[complexityId] || tiers[0] || DEFAULT_COMPLEXITY_TIERS[1];
    return (
        <section className="py-16 lg:py-20 bg-secondary/30">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="max-w-2xl mb-8">
                    <div className="overline text-primary mb-3">{t("complexity.eyebrow", "Explore your case")}</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        {t("complexity.headline_prefix", "Understand")}{" "}
                        <span className="text-primary">{t("complexity.headline_accent", "where your case might sit.")}</span>
                    </h2>
                    <p className="text-muted-foreground mt-3 text-base">
                        {t("complexity.subtitle", "Cost tracks closely with case complexity. Here's what each tier typically involves. No prices, just a clearer picture of the treatment journey.")}
                    </p>
                </div>

                <div className="inline-flex bg-card border border-border rounded-full p-1 mb-6" data-testid="complexity-toggle">
                    {tiers.map((opt) => (
                        <button
                            key={opt.id}
                            onClick={() => onComplexityChange(opt.id)}
                            data-testid={`complexity-${opt.id}`}
                            className={`text-sm font-semibold rounded-full px-5 h-10 transition-colors ${
                                complexityId === opt.id ? opt.tone : "text-foreground/70 hover:bg-secondary"
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                <div className="bg-card border border-border rounded-3xl p-6 sm:p-10 bento-shadow" data-testid="complexity-panel">
                    <div className="flex items-center gap-2 mb-5">
                        <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-full ${c.tone}`}>
                            {c.label}
                        </span>
                        <span className="text-xs text-muted-foreground">{c.typical}</span>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5 lg:gap-8">
                        <div>
                            <Clock size={20} weight="duotone" color="hsl(192 100% 31%)" />
                            <div className="text-xs text-muted-foreground uppercase tracking-wide mt-2 font-semibold">Duration</div>
                            <div className="font-display text-2xl font-bold mt-1">{c.duration}</div>
                        </div>
                        <div>
                            <Stack size={20} weight="duotone" color="hsl(192 100% 31%)" />
                            <div className="text-xs text-muted-foreground uppercase tracking-wide mt-2 font-semibold">Aligner count</div>
                            <div className="font-display text-2xl font-bold mt-1">{c.aligners}</div>
                        </div>
                        <div>
                            <Sparkle size={20} weight="duotone" color="hsl(192 100% 31%)" />
                            <div className="text-xs text-muted-foreground uppercase tracking-wide mt-2 font-semibold">Mechanics likely</div>
                            <ul className="mt-1 space-y-0.5">
                                {(c.mechanics || []).map((m) => (
                                    <li key={m} className="text-sm flex items-center gap-1.5">
                                        <CheckCircle size={12} weight="fill" className="text-primary shrink-0" /> {m}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-border flex flex-wrap items-center justify-between gap-3">
                        <div className="text-xs text-muted-foreground italic max-w-md">
                            {t("complexity.disclaimer", "We deliberately show no price here. Your doctor will give you an exact quote based on your scans and clinical assessment.")}
                        </div>
                        <Link to="/smile-assessment" className="relative group" data-testid="complexity-cta-link">
                            <span className="absolute inset-0 rounded-full bg-amber-400/40 blur-md animate-pulse pointer-events-none" aria-hidden="true" />
                            <span className="absolute -top-2 -right-2 z-10 px-2 py-0.5 rounded-full bg-amber-400 text-foreground text-[10px] font-bold tracking-wider shadow-md">
                                {t("complexity.cta_badge_label", "FREE")}
                            </span>
                            <Button
                                variant="outline"
                                className="relative h-11 px-5 rounded-full border-2 border-amber-400 bg-white hover:bg-amber-50 text-foreground font-semibold transition-all group-hover:scale-[1.03]"
                                data-testid="complexity-cta"
                            >
                                <Sparkle size={14} weight="fill" className="mr-2 text-amber-500" />
                                {t("complexity.cta_label", "FREE SMILE ASSESSMENT")}
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}


/* ================================================================
   Cost Factors
   ================================================================ */
function CostFactors({ t, arr }) {
    const items = arr("cost_factors.items_json", []);
    return (
        <section className="py-16 lg:py-20 bg-background">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="max-w-2xl mb-10">
                    <div className="overline text-primary mb-3">{t("cost_factors.eyebrow", "What shapes your final number")}</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        {t("cost_factors.headline_prefix", "Five honest factors.")}{" "}
                        <span className="text-primary">{t("cost_factors.headline_accent", "No surprises.")}</span>
                    </h2>
                </div>
                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${Math.min(items.length || 5, 5)} gap-4`}>
                    {items.map((f, i) => {
                        const Icon = resolveIcon(f.icon);
                        return (
                            <div key={f.title} className="bg-card border border-border rounded-2xl p-5 bento-shadow relative" data-testid={`factor-${i}`}>
                                <div className="absolute top-3 right-3 text-[10px] text-muted-foreground/50 font-mono">0{i + 1}</div>
                                <Icon size={24} weight="duotone" color="hsl(192 100% 31%)" />
                                <h3 className="font-display font-semibold mt-3 text-base">{f.title}</h3>
                                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


/* ================================================================
   Included section (3-column contrast)
   ================================================================ */
function IncludedSection({ t, arr }) {
    const includedItems = arr("included.included_items_json", []);
    const notIncludedItems = arr("included.notincluded_items_json", []);
    const neverItems = arr("included.never_items_json", []);
    return (
        <section className="py-16 lg:py-20 bg-secondary/30" data-testid="pricing-included-section">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="max-w-3xl mb-10 lg:mb-12">
                    <div className="overline text-primary mb-3">{t("included.eyebrow", "What's in your quote")}</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1]">
                        {t("included.headline_prefix", "Itemised before you sign.")}{" "}
                        <span className="text-primary">{t("included.headline_accent", "Honest about what isn't ours to bundle.")}</span>
                    </h2>
                    <p className="text-muted-foreground mt-4 text-base leading-relaxed">
                        {t("included.subtitle", "Three columns. One promise: every charge appears on the written quote your doctor hands you. If it's not in here, it won't appear on your bill later.")}
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
                    <div className="bg-card border border-primary/20 rounded-3xl p-7 bento-shadow" data-testid="included-card">
                        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] bg-primary/10 text-primary px-2.5 py-1 rounded-full mb-5">
                            <CheckCircle size={11} weight="fill" /> {t("included.included_pill_label", "Included in every quote")}
                        </div>
                        <h3 className="font-display text-xl font-bold mb-4 leading-tight">
                            {t("included.included_heading", "Comprehensive, end to end")}
                        </h3>
                        <ul className="space-y-2.5">
                            {includedItems.map((it) => (
                                <li key={it} className="flex gap-2.5 text-sm">
                                    <CheckCircle size={16} weight="fill" className="text-primary shrink-0 mt-0.5" />
                                    <span>{it}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="bg-card border border-border rounded-3xl p-7" data-testid="not-included-card">
                        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] bg-secondary text-foreground/70 px-2.5 py-1 rounded-full mb-5">
                            <Question size={11} weight="fill" /> {t("included.notincluded_pill_label", "Discussed at consultation")}
                        </div>
                        <h3 className="font-display text-xl font-bold mb-4 leading-tight">
                            {t("included.notincluded_heading", "Clinic specific add ons")}
                        </h3>
                        <ul className="space-y-2.5">
                            {notIncludedItems.map((it) => (
                                <li key={it} className="flex gap-2.5 text-sm text-muted-foreground">
                                    <CaretDown size={14} className="text-muted-foreground/60 shrink-0 mt-1" />
                                    <span>{it}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-xs text-muted-foreground italic mt-5 pt-4 border-t border-border">
                            {t("included.notincluded_footer_note", "Each Invisique™ partner sets their own policy on retainers and refinements. Most are very fair. Ask explicitly during consultation.")}
                        </p>
                    </div>

                    <div className="bg-card border border-emerald-200/80 rounded-3xl p-7 bento-shadow relative overflow-hidden" data-testid="never-on-bill-card">
                        <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-emerald-100/60 blur-2xl pointer-events-none" aria-hidden="true" />
                        <div className="relative">
                            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full mb-5">
                                <SealCheck size={11} weight="fill" /> {t("included.never_pill_label", "Never on your bill")}
                            </div>
                            <h3 className="font-display text-xl font-bold mb-4 leading-tight">
                                {t("included.never_heading", "What other brands tack on, we don't.")}
                            </h3>
                            <ul className="space-y-2.5">
                                {neverItems.map((it) => (
                                    <li key={it} className="flex gap-2.5 text-sm">
                                        <X size={16} weight="bold" className="text-emerald-600 shrink-0 mt-0.5" />
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-xs text-emerald-700/80 italic mt-5 pt-4 border-t border-emerald-100">
                                {t("included.never_footer_note", "If anything outside your written quote ever comes up, it's a conversation — not a surprise charge.")}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}


/* ================================================================
   Financing strip
   ================================================================ */
function FinancingStrip({ t }) {
    return (
        <section className="py-12 lg:py-16 bg-primary text-primary-foreground">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.5fr_1fr] gap-8 items-center">
                <div>
                    <div className="overline text-white/70 mb-3">{t("financing.eyebrow", "Affordable, on your terms")}</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                        {t("financing.headline_prefix", "Most Invisique™ partner clinics offer EMI plans")}{" "}
                        <span className="text-amber-300">{t("financing.headline_accent", "tailored to your budget.")}</span>
                    </h2>
                    <p className="text-white/80 mt-4 text-base max-w-lg">
                        {t("financing.blurb", "From 3 month interest free options to 24 month low EMI plans via major Indian banks and NBFCs. Your monthly outlay can be lower than your phone bill.")}
                    </p>
                </div>
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6">
                    <Wallet size={28} weight="duotone" className="text-white mb-3" />
                    <div className="text-2xl font-display font-bold leading-tight">
                        {t("financing.card_title", "3 / 6 / 12 / 24 month plans")}
                    </div>
                    <p className="text-white/75 text-sm mt-2">
                        {t("financing.card_note", "Subject to bank or NBFC approval. Your clinic will walk you through partner options at consultation.")}
                    </p>
                </div>
            </div>
        </section>
    );
}


/* ================================================================
   FAQ
   ================================================================ */
function FaqSection({ t, arr }) {
    const items = arr("faq.items_json", []);
    const [openIdx, setOpenIdx] = useState(0);
    return (
        <section className="py-16 lg:py-24 bg-background">
            <div className="max-w-3xl mx-auto px-5 sm:px-8">
                <div className="text-center mb-10">
                    <div className="overline text-primary mb-3">{t("faq.eyebrow", "FAQ")}</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        {t("faq.headline_prefix", "Real questions.")}{" "}
                        <span className="text-primary">{t("faq.headline_accent", "Honest answers.")}</span>
                    </h2>
                </div>
                <div className="space-y-2">
                    {items.map((f, i) => {
                        const open = openIdx === i;
                        return (
                            <div key={f.q} className="border border-border rounded-2xl overflow-hidden bg-card" data-testid={`faq-${i}`}>
                                <button
                                    onClick={() => setOpenIdx(open ? -1 : i)}
                                    className="w-full text-left p-5 flex items-start justify-between gap-3 hover:bg-secondary/30 transition-colors"
                                    data-testid={`faq-toggle-${i}`}
                                >
                                    <h3 className="font-display font-semibold text-base sm:text-lg pr-4">{f.q}</h3>
                                    <CaretDown
                                        size={18}
                                        className={`text-primary shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                                    />
                                </button>
                                {open && (
                                    <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed -mt-1" data-testid={`faq-body-${i}`}>
                                        {f.a}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


/* ================================================================
   Pricing testimonial — iter100.207 admin-managed via Patient Spotlights
   ================================================================ */
function PricingTestimonial({ t }) {
    // Fallback data mirrors the previous `pricing_testimonial.*` CMS keys so
    // the section keeps rendering even if the API request fails.
    const [s, setS] = useState({
        image_url: t("pricing_testimonial.image_url", "/marketing/p9.png"),
        name: t("pricing_testimonial.name", "Aparna S."),
        city_meta: t("pricing_testimonial.meta", "Bengaluru · Quoted March, completed November"),
        quote: t("pricing_testimonial.quote", "I'd asked three other brands for a quote — every one came back with a 'starting at' number and added charges later. My orthodontist showed me the full Invisique™ quote on paper before I committed. Same number we signed for, nine months later."),
        badge_top: t("pricing_testimonial.badge_top", "Quote held to"),
        badge_bot: t("pricing_testimonial.badge_bot", "The rupee"),
        verified_pill_label: t("pricing_testimonial.verified_pill_label", "Verified patient"),
    });
    useEffect(() => {
        api.get("/public/spotlights/pricing")
            .then((r) => {
                if (r.data?.item) setS(r.data.item);
            })
            .catch(() => { /* fallback stays */ });
    }, []);
    return (
        <section
            className="relative py-16 lg:py-24 overflow-hidden border-t border-border/60"
            data-testid="pricing-testimonial"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/40 via-background to-primary/5" aria-hidden="true" />
            <div className="absolute -top-32 -right-24 w-80 h-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-amber-200/30 blur-3xl pointer-events-none" aria-hidden="true" />

            <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-amber-700 text-[10px] font-bold tracking-[0.22em] uppercase">
                        <Sparkle size={11} weight="fill" /> {t("pricing_testimonial.eyebrow", "The quoting experience")}
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-stretch">
                    <div className="lg:col-span-5 relative flex justify-center lg:justify-start">
                        <div className="relative w-full max-w-[380px]">
                            <div className="absolute top-5 left-5 w-full h-full rounded-[28px] bg-primary" aria-hidden="true" />
                            <div className="absolute -top-3 -left-3 w-20 h-20 rounded-2xl bg-amber-300/45 border border-amber-300/60" aria-hidden="true" />

                            <div className="relative rounded-[28px] overflow-hidden bento-shadow ring-1 ring-border/50">
                                <img
                                    src={s.image_url}
                                    alt={t("pricing_testimonial.image_alt", `${s.name}, verified Invisique™ patient`)}
                                    className="w-full aspect-[4/5] object-cover"
                                    loading="lazy"
                                    data-testid="pricing-spotlight-portrait"
                                />
                                <div className="absolute bottom-4 left-4">
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-md border border-white/40 shadow-lg">
                                        <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                                        <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-foreground">
                                            {s.verified_pill_label || "Verified patient"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {(s.badge_top || s.badge_bot) && (
                                <div className="absolute -top-4 -right-4 sm:right-2 px-4 py-3 rounded-2xl bg-card border border-emerald-200/80 shadow-xl" data-testid="pricing-spotlight-badge">
                                    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-700">
                                        {s.badge_top}
                                    </div>
                                    <div className="font-display text-lg font-bold tracking-tight text-foreground leading-none mt-1">
                                        {s.badge_bot}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="lg:col-span-7 relative flex flex-col justify-center">
                        <Quotes
                            size={120}
                            weight="fill"
                            className="absolute -top-8 -left-2 text-primary/10 pointer-events-none select-none hidden sm:block"
                            aria-hidden="true"
                        />
                        <p className="relative font-display text-xl sm:text-2xl lg:text-[1.8rem] leading-snug tracking-tight font-medium text-foreground" data-testid="pricing-spotlight-quote">
                            &ldquo;{s.quote}&rdquo;
                        </p>

                        <div className="mt-7 flex items-center gap-3">
                            <div className="w-10 h-px bg-primary/50" aria-hidden="true" />
                            <div>
                                <div className="font-semibold text-base text-foreground" data-testid="pricing-spotlight-name">{s.name}</div>
                                <div className="text-sm text-muted-foreground mt-0.5" data-testid="pricing-spotlight-meta">{s.city_meta}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}


/* ================================================================
   Sticky assessment CTA
   ================================================================ */
function StickyAssessmentBar({ t }) {
    const [scrolled, setScrolled] = useState(false);
    const [ctaVisible, setCtaVisible] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 600);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });

        let observer;
        const closer = document.querySelector('[data-testid="pricing-counsellor-block"]');
        if (closer && typeof IntersectionObserver !== "undefined") {
            observer = new IntersectionObserver(
                ([entry]) => setCtaVisible(entry.isIntersecting),
                { threshold: 0.15 },
            );
            observer.observe(closer);
        }
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (observer) observer.disconnect();
        };
    }, []);

    if (dismissed || !scrolled || ctaVisible) return null;

    return (
        <div className="fixed bottom-4 left-0 right-0 z-40 px-4 pointer-events-none" data-testid="pricing-sticky-cta">
            <div className="max-w-2xl mx-auto pointer-events-auto">
                <div className="relative flex items-center gap-3 p-2 pl-4 sm:pl-5 bg-white border border-border/70 rounded-full shadow-[0_20px_50px_-15px_rgba(0,45,57,0.35)] backdrop-blur-sm animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <Sparkle size={16} weight="fill" className="text-amber-500 shrink-0 hidden sm:block" />
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold tracking-widest uppercase text-amber-700 leading-none">
                            {t("sticky_cta.eyebrow", "Free · 60 seconds")}
                        </div>
                        <div className="text-[12.5px] sm:text-sm font-semibold text-foreground leading-tight mt-0.5 truncate">
                            {t("sticky_cta.title", "See where your case sits.")}
                        </div>
                    </div>
                    <Link to="/smile-assessment" data-testid="pricing-sticky-cta-link">
                        <Button
                            size="sm"
                            className="h-9 sm:h-10 px-4 sm:px-5 rounded-full bg-gradient-to-r from-primary to-[#003E50] text-white font-semibold tracking-wide hover:from-[#003E50] hover:to-primary shadow-md shrink-0 text-xs sm:text-sm"
                            data-testid="pricing-sticky-cta-btn"
                        >
                            <Sparkle size={13} weight="fill" className="mr-1.5 text-amber-300" />
                            <span className="hidden sm:inline">{t("sticky_cta.btn_label_lg", "Smile assessment")}</span>
                            <span className="sm:hidden">{t("sticky_cta.btn_label_sm", "Assessment")}</span>
                            <ArrowRight size={13} weight="bold" className="ml-1.5" />
                        </Button>
                    </Link>
                    <button
                        type="button"
                        onClick={() => setDismissed(true)}
                        aria-label="Dismiss"
                        data-testid="pricing-sticky-cta-dismiss"
                        className="w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors shrink-0"
                    >
                        <X size={14} weight="bold" />
                    </button>
                </div>
            </div>
        </div>
    );
}


/* ================================================================
   Retainer lifecycle band
   ================================================================ */
function RetainerLifecycle({ t, arr }) {
    const stats = arr("retainer.stats_json", []);
    return (
        <section className="py-10 lg:py-12 bg-background" data-testid="pricing-retainer-band">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="rounded-3xl border border-border/70 bg-card p-5 lg:p-6 bento-shadow flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
                    <div className="lg:max-w-xs shrink-0">
                        <div className="overline text-primary mb-2">{t("retainer.eyebrow", "After treatment")}</div>
                        <div className="font-display text-lg sm:text-xl font-bold leading-snug">
                            {t("retainer.headline_prefix", "Retainers")}{" "}
                            <span className="text-primary">{t("retainer.headline_accent", "are in your quote.")}</span>
                        </div>
                    </div>

                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-0 lg:divide-x lg:divide-border/60">
                        {stats.map((s, i) => (
                            <RetainerInlineStat key={i} iconName={s.icon} label={s.label} value={s.value} />
                        ))}
                    </div>

                    <Link
                        to="/infinite"
                        className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline shrink-0 self-start lg:self-center"
                        data-testid="pricing-retainer-band-deep-link"
                    >
                        {t("retainer.deep_link_label", "See the Infinite Retainers™ programme")}
                        <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

const RetainerInlineStat = ({ iconName, label, value }) => {
    const Icon = resolveIcon(iconName);
    return (
        <div className="flex items-start gap-3 sm:px-5 lg:first:pl-0 lg:last:pr-0">
            <div className="w-9 h-9 rounded-full bg-primary/10 grid place-items-center shrink-0">
                <Icon size={16} weight="duotone" className="text-primary" />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-0.5">{label}</div>
                <div className="text-sm font-semibold leading-snug">{value}</div>
            </div>
        </div>
    );
};


/* ================================================================
   Counsellor block
   ================================================================ */
function CounsellorBlock({ t }) {
    return (
        <section className="py-14 lg:py-20 bg-secondary/30 border-t border-border/60" data-testid="pricing-counsellor-block">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-amber-700 text-[10px] font-bold tracking-[0.22em] uppercase">
                        <Sparkle size={11} weight="fill" /> {t("counsellor.eyebrow", "Still have questions?")}
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-[2rem] font-bold tracking-tight leading-[1.1] mt-4 max-w-2xl mx-auto">
                        {t("counsellor.headline_prefix", "Prefer a quick chat")}{" "}
                        <span className="text-primary">{t("counsellor.headline_accent", "before you commit?")}</span>
                    </h2>
                    <p className="text-muted-foreground mt-3 text-base max-w-xl mx-auto leading-relaxed">
                        {t("counsellor.subtitle", "Leave your number — an Invisique™ Patient Engagement Specialist will call to answer questions and help you find the right partner clinic.")}
                    </p>
                </div>
                <CounsellorCallbackCard />
            </div>
        </section>
    );
}


/* ================================================================
   Honest quote sample
   ================================================================ */
function HonestQuoteSample({ t, arr }) {
    const lines = arr("honest_quote.lines_json", []);
    const annotations = arr("honest_quote.annotations_json", []);
    return (
        <section className="py-16 lg:py-20 bg-background" data-testid="pricing-honest-quote-sample">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="max-w-3xl mb-10 lg:mb-12">
                    <div className="overline text-primary mb-3">{t("honest_quote.eyebrow", "An honest quote")}</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1]">
                        {t("honest_quote.headline_prefix", "Here's what a written quote")}{" "}
                        <span className="text-primary">{t("honest_quote.headline_accent", "actually looks like.")}</span>
                    </h2>
                    <p className="text-muted-foreground mt-4 text-base leading-relaxed">
                        {t("honest_quote.subtitle", "No \"starting at\" fineprint. No surprise line items added at the chair. Every charge appears on the paper your doctor signs — and nothing else does.")}
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5 }}
                        className="lg:col-span-7 relative bg-white rounded-3xl border border-border/70 bento-shadow overflow-hidden"
                        data-testid="pricing-honest-quote-receipt"
                    >
                        <div className="flex items-center justify-between px-7 py-5 border-b border-border bg-secondary/30">
                            <div className="flex items-center gap-2.5">
                                <div className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center">
                                    <FileText size={20} weight="duotone" className="text-primary" />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">
                                        {t("honest_quote.receipt_title", "Sample written quote")}
                                    </div>
                                    <div className="text-xs font-mono text-foreground/80">
                                        {t("honest_quote.receipt_id", "INV-QUOTE-249301")}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                    {t("honest_quote.receipt_issued_label", "Issued")}
                                </div>
                                <div className="text-xs font-mono text-foreground/80">
                                    {t("honest_quote.receipt_issued_value", "14 Mar 2026")}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 px-7 py-4 border-b border-border/60">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Patient</div>
                                <div className="text-sm font-semibold mt-0.5">
                                    {t("honest_quote.patient_name", "Aparna S.")}{" "}
                                    <span className="text-muted-foreground font-normal">· {t("honest_quote.patient_location", "Bengaluru")}</span>
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Clinic</div>
                                <div className="text-sm font-semibold mt-0.5">
                                    {t("honest_quote.clinic_name", "Dr. P. Kumar")}{" "}
                                    <span className="text-muted-foreground font-normal">· {t("honest_quote.clinic_qualifier", "Certified partner")}</span>
                                </div>
                            </div>
                        </div>

                        <div className="px-7 py-5">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-3">What you&apos;re paying for</div>
                            <ul className="space-y-2.5">
                                {lines.map((line) => (
                                    <li key={line} className="flex items-start gap-2.5 text-sm">
                                        <CheckCircle size={16} weight="fill" className="text-primary shrink-0 mt-0.5" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="px-7 py-5 border-t-2 border-dashed border-border/70 bg-secondary/20 flex items-center justify-between gap-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                    {t("honest_quote.final_label", "Final number")}
                                </div>
                                <div className="font-display text-lg font-bold leading-tight mt-1">
                                    {t("honest_quote.final_value", "Set by your doctor")}
                                </div>
                            </div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/80 text-emerald-700 text-[11px] font-bold tracking-wider uppercase">
                                <SealCheck size={13} weight="fill" /> {t("honest_quote.validation_pill", "COE-validated")}
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        className="lg:col-span-5 space-y-4"
                    >
                        {annotations.map((p) => {
                            const Icon = resolveIcon(p.icon);
                            const slug = String(p.title || "").toLowerCase().replace(/[^a-z]+/g, "-");
                            return (
                                <div
                                    key={p.title}
                                    className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-border/60 hover:border-primary/30 transition-colors"
                                    data-testid={`pricing-honest-quote-annotation-${slug}`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-primary text-white grid place-items-center shadow-sm ring-2 ring-secondary/40 shrink-0">
                                        <Icon size={16} weight="fill" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-display text-sm font-bold leading-tight">{p.title}</div>
                                        <div className="text-[12px] text-muted-foreground leading-snug mt-1">{p.desc}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
